// eslint-disable-next-line no-unused-vars
import React from 'react';
import { array, bool, func, object } from 'prop-types';
import {
  DataTableSkeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow
} from '@carbon/react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { LockKeyhole, Pencil, Trash } from 'lucide-react';

import PromptModal from 'Containers/PromptModal/PromptModal';
import OfferStatusBadge from 'Components/Offers/OfferStatusBadge';
import { NWPagination, NWTabsNavigation } from 'Components/shared';

const StyledOffers = styled.main`
  min-width: 0;

  .pageTitle {
    border-bottom: 2px solid ${ props => props.theme.products.colors.border };
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;

    h3 {
      margin: 0 0 0.35rem;
    }
  }

  .empty-saved-tab {
    min-height: 160px;
  }

  .table-wrap {
    overflow-x: auto;
  }

  .cds--data-table {
    border: 1px solid #e2e2e2;
    font-size: 16px;

    thead th {
      background: #f5f7f9;
      color: #3a3a3a;
      font-size: 16px;
      font-weight: 600;

      &:first-of-type .cds--table-header-label {
        text-align: left;
      }

      &:last-of-type .cds--table-header-label {
        text-align: right;
      }

      .cds--table-header-label {
        font-size: 16px;
        font-weight: 500;
        text-align: center;
      }
    }

    tbody tr td {
      background: #fff;
      text-align: center;

      &:first-of-type {
        text-align: left;
      }

      &:last-of-type {
        text-align: right;
      }
    }
  }

  .offer-name {
    font-weight: 600;
  }

  .row-actions {
    align-items: center;
    display: flex;
    gap: 5px;
    justify-content: flex-end;
    white-space: nowrap;

    button {
      background: transparent;
      border: 0;
      color: ${ props => props.theme.colors.red };
      cursor: pointer;
      display: inline-flex;
      padding: 7px 3px;
    }

    .open {
      background: #fff;
      border: 1px solid #dde6f2;
      color: #3a3a3a;
      cursor: pointer;
      padding: 7px;

      &:hover {
        background: #d0ddef;
      }
    }

    .delete {
      background: #fef2e8;
      border: 1px solid #dde6f2;
      color: #3a3a3a;
      padding: 7px;

      &:hover {
        background: #fbb2ac;
      }
    }
  }

  .private-note {
    align-items: center;
    background: #edf8f5;
    border: 1px solid #73b5a5;
    border-radius: 4px;
    color: #164f43;
    display: flex;
    font-size: 0.8rem;
    gap: 0.65rem;
    margin-top: 1rem;
    padding: 0.75rem 1rem;
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .table-wrap {
      overflow-x: visible;
    }

    .cds--data-table {
      border: none;

      > thead {
        display: none;
      }

      > tbody {
        background: #fff;

        tr {
          border: 1px solid #e2e2e2;
          display: flex;
          flex-direction: column;
          height: auto;
          margin-top: 20px;
          position: relative;

          td {
            border: transparent;
            text-align: left;

            &:first-of-type {
              background: #f5f7f9;
              position: relative;
            }

            &:last-of-type {
              height: 50px;
              text-align: left;
            }
          }

          &.empty-row {
            margin-top: 0;

            td {
              background: #fff;
              padding: 1rem;
            }
          }
        }
      }
    }

    .row-actions {
      justify-content: flex-start;
    }
  }
`;

const StyledTableCell = styled(TableCell)`
  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    &[data-label] {
      align-items: center;
      display: flex;
      min-height: 40px;
      padding-left: 175px;

      &:before {
        content: "${ props => props.name }:";
        font-weight: 600;
        left: 10px;
        overflow: hidden;
        position: absolute;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 160px;
      }
    }
  }
`;

const Offers = ({
  isHydrated,
  isPrivateTab,
  savedOffers,
  savedOffersLoading,
  savedOffersMeta,
  privateOffers,
  privateOffersMeta,
  offerToDelete,
  onOpen,
  onDeleteRequest,
  onDeleteConfirm,
  onDeleteCancel,
  onOffersPageChange,
  getOffersPageHref
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const headers = [
    { key: 'offer', label: t('offers.offerColumn') },
    { key: 'client', label: t('offers.client') },
    { key: 'createdAt', label: t('offers.savedAt') },
    { key: 'updatedAt', label: t('offers.updatedAt') },
    { key: 'validUntil', label: t('offers.validUntil') },
    { key: 'productCount', label: t('offers.productCount') },
    { key: 'total', label: t('offers.total') },
    { key: 'status', label: t('common.status') },
    { key: 'actions', label: t('common.actions') }
  ];
  const displayedOffers = isPrivateTab ? privateOffers : savedOffers;
  const paginationMeta = isPrivateTab ? privateOffersMeta : savedOffersMeta;
  const isLoading = isPrivateTab ? !isHydrated : savedOffersLoading;
  const emptyMessage = t(isPrivateTab ? 'offers.noPrivateOffers' : 'offers.noSavedOffers');

  return (
    <StyledOffers theme={ theme }>
      <PromptModal
        modalOpen={ !!offerToDelete }
        onCloseModal={ onDeleteCancel }
        onConfirm={ onDeleteConfirm }
        header={ (
          <h4>{ t(isPrivateTab ? 'offers.deletePrivateOffer' : 'offers.deleteSavedOffer') }</h4>
        ) }
        body={ (
          <p>
            { t(isPrivateTab
              ? 'offers.deletePrivateOfferConfirmation'
              : 'offers.deleteSavedOfferConfirmation') }
          </p>
        ) }
        confirmButton={ t('common.delete') }
        className="DeletePrivateOfferModal"
      />
      <div className="pageTitle">
        <h3>{ t('offers.myOffers') }</h3>
      </div>
      <NWTabsNavigation
        links={ [
          { to: '/offers', label: t('offers.savedForLater'), end: true },
          { to: '/offers/private', label: t('offers.privateBrowser') }
        ] }
      />

      <section>
        { isLoading ? (
          <DataTableSkeleton columnCount={ headers.length } showToolbar={ false } showHeader={ false } />
        ) : (
          <div className="table-wrap">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    { headers.map(header => <TableHeader key={ header.key }>{ header.label }</TableHeader>) }
                  </TableRow>
                </TableHead>
                <TableBody>
                  { !displayedOffers.length && (
                    <TableRow className="empty-row">
                      <TableCell colSpan={ headers.length }>{ emptyMessage }</TableCell>
                    </TableRow>
                  ) }
                  { displayedOffers.map(offer => (
                    <TableRow key={ offer.id }>
                      <StyledTableCell data-label="offer" name={ headers[0].label } className="offer-name">
                        { offer.name }
                      </StyledTableCell>
                      <StyledTableCell data-label="client" name={ headers[1].label }>
                        { offer.clientDisplay }
                      </StyledTableCell>
                      <StyledTableCell data-label="createdAt" name={ headers[2].label }>
                        { offer.createdAtDisplay }
                      </StyledTableCell>
                      <StyledTableCell data-label="updatedAt" name={ headers[3].label }>
                        { offer.updatedAtDisplay }
                      </StyledTableCell>
                      <StyledTableCell data-label="validUntil" name={ headers[4].label }>
                        { offer.validUntilDisplay }
                      </StyledTableCell>
                      <StyledTableCell data-label="productCount" name={ headers[5].label }>
                        { offer.numberOfProducts }
                      </StyledTableCell>
                      <StyledTableCell data-label="total" name={ headers[6].label }>
                        <strong>{ offer.totalDisplay }</strong>
                      </StyledTableCell>
                      <StyledTableCell data-label="status" name={ headers[7].label }>
                        <OfferStatusBadge status={ offer.isExpired ? 'expired' : 'valid' } />
                      </StyledTableCell>
                      <StyledTableCell data-label="actions" name={ headers[8].label }>
                        <div className="row-actions">
                          <button
                            className="open"
                            type="button"
                            aria-label={ t('offers.openOffer') }
                            title={ t('common.edit') }
                            onClick={ () => onOpen(offer) }
                          >
                            <Pencil size={ 18 } />
                          </button>
                          <button
                            className="delete"
                            type="button"
                            aria-label={ t(isPrivateTab
                              ? 'offers.deletePrivateOffer'
                              : 'offers.deleteSavedOffer') }
                            title={ t('common.delete') }
                            onClick={ () => onDeleteRequest(offer) }
                          >
                            <Trash size={ 18 } />
                          </button>
                        </div>
                      </StyledTableCell>
                    </TableRow>
                  )) }
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        ) }
        { isPrivateTab && (
          <p className="private-note">
            <LockKeyhole size={ 18 } />
            { t('offers.privateBrowserNotice') }
          </p>
        ) }
        <NWPagination
          page={ paginationMeta.page }
          itemsPerPage={ paginationMeta.limit }
          itemsCount={ paginationMeta.totalItems }
          pageCount={ paginationMeta.totalPages }
          hrefBuilder={ getOffersPageHref }
          onPageChange={ onOffersPageChange }
          loading={ isLoading }
          itemsName={ t('offers.offers') }
        />
      </section>
    </StyledOffers>
  );
};

Offers.propTypes = {
  isHydrated: bool,
  isPrivateTab: bool,
  savedOffers: array,
  savedOffersLoading: bool,
  savedOffersMeta: object,
  privateOffers: array,
  privateOffersMeta: object,
  offerToDelete: object,
  onOpen: func,
  onDeleteRequest: func,
  onDeleteConfirm: func,
  onDeleteCancel: func,
  onOffersPageChange: func,
  getOffersPageHref: func
};

export default Offers;
